import { buildPrompt } from './prompt.js';
import { cardDataSchema, SHOT_MAX_SECONDS, SHOT_MIN_SECONDS, shotDataSchema, } from './schemas.js';
import { z } from 'zod';
/**
 * The Director: ChatGPT plans the film shot by shot like a film director (look book, character and location
 * bibles, and per shot: size, angle, lens, movement, lighting, grade, blocking, dialogue, sound…). The plan is
 * edited by the team and then turned into storyboard shots (`planToItems`), whose prompts go to FreeVideo.
 */
export const SHOT_SIZES = ['ECU', 'CU', 'MCU', 'MS', 'MWS', 'WS', 'EWS'];
export const SHOT_SIZE_WORDS = {
    ECU: 'An extreme close-up',
    CU: 'A close-up',
    MCU: 'A medium close-up',
    MS: 'A medium shot',
    MWS: 'A medium-wide shot',
    WS: 'A wide shot',
    EWS: 'An extreme wide shot',
};
export const CAMERA_ANGLES = [
    'eye level',
    'low angle',
    'high angle',
    'overhead',
    'dutch angle',
    'POV',
    'over the shoulder',
];
const ANGLE_WORDS = {
    'eye level': 'at eye level',
    'low angle': 'from a low angle',
    'high angle': 'from a high angle',
    overhead: 'from a top-down overhead angle',
    'dutch angle': 'at a tilted dutch angle',
    POV: 'from a point-of-view angle',
    'over the shoulder': 'over the shoulder',
};
export const CAMERA_MOVES = [
    'static',
    'push in',
    'pull out',
    'pan left',
    'pan right',
    'tilt up',
    'tilt down',
    'dolly in',
    'dolly out',
    'truck left',
    'truck right',
    'tracking',
    'crane up',
    'crane down',
    'arc',
    'handheld',
    'zoom in',
    'zoom out',
];
const MOVE_VERBS = {
    static: '',
    'push in': 'pushes in',
    'pull out': 'pulls out',
    'pan left': 'pans left',
    'pan right': 'pans right',
    'tilt up': 'tilts up',
    'tilt down': 'tilts down',
    'dolly in': 'dollies in',
    'dolly out': 'dollies out',
    'truck left': 'trucks left',
    'truck right': 'trucks right',
    tracking: 'tracks with the action',
    'crane up': 'cranes up',
    'crane down': 'cranes down',
    arc: 'arcs around the subject',
    handheld: '',
    'zoom in': 'zooms in',
    'zoom out': 'zooms out',
};
export const MOVE_SPEEDS = ['very slow', 'slow', 'medium', 'fast'];
const SPEED_WORDS = {
    'very slow': 'very slowly',
    slow: 'slowly',
    medium: 'at a steady pace',
    fast: 'quickly',
};
export const MOVE_AMPLITUDES = ['small', 'medium', 'large'];
const s = (max = 3000) => z.string().max(max).catch('').default('');
const oneOf = (values, fallback) => z.enum(values).catch(fallback).default(fallback);
const lookSchema = z.object({
    visualStyle: s(800),
    palette: z
        .array(z.object({
        name: s(60),
        hex: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .catch('#888888'),
    }))
        .max(12)
        .catch([])
        .default([]),
    lighting: s(800),
    lensKit: s(500),
    cameraLanguage: s(800),
    pacing: s(600),
    music: s(600),
    soundDesign: s(600),
    productionDesign: s(800),
    wardrobe: s(800),
});
export const planCharacterSchema = z.object({
    /** As in the character library (a new name: created in the library when the plan is sent to the storyboard). */
    name: z.string().min(1).max(120).catch('Character'),
    role: s(500),
    /** Fixed, very specific look — used word for word in every shot with this character. */
    appearance: s(1500),
    voice: s(300),
});
export const planLocationSchema = z.object({
    name: z.string().min(1).max(120).catch('Location'),
    /** Fixed description (a noun phrase), used word for word in every shot there. */
    description: s(1500),
});
export const planLineSchema = z.object({
    /** A character name from the bible, or e.g. "A narrator". */
    speaker: s(120),
    /** Exact words, in the language's own script. */
    text: s(1000),
    /** says / shouts / whispers / says into the phone … */
    verb: s(80),
    /** How it is said: emotion and tone. */
    emotion: s(200),
    /** The user's script line this comes from, exactly as written (the part of it, when a line is split); '' if invented. */
    source: s(1000),
});
/** A line of the user's script that breaks a rule. The line is NOT changed by the director; the team decides. */
export const planWarningSchema = z.object({
    shotNumber: z.number().int().min(0).max(200).catch(0).default(0),
    speaker: s(120),
    /** The exact words concerned, as they are in the plan's dialogue. */
    quote: s(1000),
    /** e.g. "health claim not in allowed claims", "personal-result testimonial", "guaranteed result". */
    rule: s(200),
    why: s(600),
    /** A drop-in replacement line in the same language and script. */
    saferWording: s(1000),
    /** open: not decided; applied: the safer wording was put in; kept: the team keeps the line as written. */
    status: oneOf(['open', 'applied', 'kept'], 'open'),
});
export const planShotSchema = z.object({
    kind: oneOf(['shot', 'card'], 'shot'),
    /** Cards only: the words, one row per line. */
    cardText: s(300),
    title: s(120),
    seconds: z.number().min(1).max(SHOT_MAX_SECONDS).catch(6).default(6),
    location: s(120),
    timeOfDay: s(120),
    shotSize: oneOf(SHOT_SIZES, 'MS'),
    angle: oneOf(CAMERA_ANGLES, 'eye level'),
    lensMm: z.number().min(8).max(400).catch(35).default(35),
    depthOfField: s(200),
    movement: z
        .object({
        type: oneOf(CAMERA_MOVES, 'static'),
        speed: oneOf(MOVE_SPEEDS, 'slow'),
        amplitude: oneOf(MOVE_AMPLITUDES, 'small'),
        description: s(500),
    })
        .catch(() => ({ type: 'static', speed: 'slow', amplitude: 'small', description: '' }))
        .prefault({}),
    composition: s(800),
    /** What happens from the first frame to the last. [Name] for characters, [Product] for the product. */
    blocking: s(2000),
    performance: s(600),
    lighting: s(800),
    colorGrade: s(300),
    setDressing: s(800),
    characters: z.array(z.string().max(120)).max(8).catch([]).default([]),
    productInShot: z.boolean().catch(false).default(false),
    lines: z.array(planLineSchema).max(8).catch([]).default([]),
    sound: s(800),
    music: s(500),
    transition: s(300),
    continuity: s(800),
    startFromPrevious: z.boolean().catch(false).default(false),
    referencePictures: z
        .array(z.object({
        type: oneOf(['character', 'product', 'shot'], 'character'),
        /** Character name (type character). */
        name: s(120),
        /** Shot number whose last frame is used (type shot). */
        shotNumber: z.number().int().min(0).max(200).catch(0).default(0),
    }))
        .max(3)
        .catch([])
        .default([]),
    noOnScreenText: z.boolean().catch(true).default(true),
});
export const directorPlanSchema = z.object({
    title: s(200),
    logline: s(600),
    beats: z.array(z.string().max(600)).max(20).catch([]).default([]),
    look: lookSchema.catch(() => lookSchema.parse({})).prefault({}),
    characters: z.array(planCharacterSchema).max(12).catch([]).default([]),
    locations: z.array(planLocationSchema).max(12).catch([]).default([]),
    shots: z.array(planShotSchema).max(60).catch([]).default([]),
    warnings: z.array(planWarningSchema).max(60).catch([]).default([]),
});
/** Labels that start a script line but are not speakers. */
const NOT_SPEAKERS = /^(scene|location|setting|note|notes|shot|cut|card|title|music|sound|sfx|cta|camera)\b/i;
/**
 * The dialogue lines of a script written as "Name: (direction) words". Scene headings and other labels are skipped;
 * lines without a "Name:" are description, not dialogue.
 */
export function parseScriptDialogue(script) {
    const out = [];
    for (const raw of script.split(/\r?\n/)) {
        const m = /^\s*([^:()"“”]{1,40}?)\s*:\s*(.+?)\s*$/.exec(raw);
        if (!m)
            continue;
        const speaker = m[1].trim();
        if (!speaker || NOT_SPEAKERS.test(speaker) || /^\d/.test(speaker))
            continue;
        const directions = [];
        const rest = m[2].replace(/\(([^)]*)\)/g, (_, d) => {
            directions.push(d.trim());
            return ' ';
        });
        const text = rest.replace(/^[\s"“”']+|[\s"“”']+$/g, '').replace(/\s+/g, ' ');
        if (text)
            out.push({ speaker, text, direction: directions.join('; ') });
    }
    return out;
}
/** For comparing dialogue: lower case, no punctuation (incl. । and quotes), single spaces. */
export function normaliseDialogue(t) {
    return t
        .normalize('NFC')
        .toLowerCase()
        .replace(/[\p{P}\p{S}।॥]+/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Script lines that do not reach the plan word for word, checked against the plan lines' `source` (the original
 * Roman/Hinglish text each plan line comes from), in order. A line split over consecutive plan lines still counts.
 */
export function missingScriptLines(script, plan) {
    const lines = typeof script === 'string' ? parseScriptDialogue(script) : script;
    const sources = normaliseDialogue(plan.shots
        .flatMap((s) => s.lines)
        .map((l) => l.source)
        .join(' '));
    const missing = [];
    let from = 0;
    for (const line of lines) {
        const want = normaliseDialogue(line.text);
        const at = want ? sources.indexOf(want, from) : -1;
        if (at < 0)
            missing.push(line);
        else
            from = at + want.length;
    }
    return missing;
}
/** Puts a warning's safer wording into the plan: the line containing the quote (in the warning's shot first). */
export function applySaferWording(plan, index) {
    const w = plan.warnings[index];
    if (!w || !w.saferWording.trim())
        return plan;
    const quote = normaliseDialogue(w.quote);
    const order = [w.shotNumber - 1, ...plan.shots.map((_, i) => i).filter((i) => i !== w.shotNumber - 1)];
    const shots = plan.shots.map((s) => ({ ...s, lines: [...s.lines] }));
    for (const si of order) {
        const shot = shots[si];
        if (!shot)
            continue;
        const li = shot.lines.findIndex((l) => quote && normaliseDialogue(l.text).includes(quote));
        if (li < 0)
            continue;
        const line = shot.lines[li];
        // The whole line, or only the quoted part when the quote is a part of it.
        const whole = normaliseDialogue(line.text) === quote || !line.text.includes(w.quote.trim());
        shot.lines[li] = {
            ...line,
            text: whole ? w.saferWording.trim() : line.text.replace(w.quote.trim(), w.saferWording.trim()),
        };
        break;
    }
    return {
        ...plan,
        shots,
        warnings: plan.warnings.map((x, i) => (i === index ? { ...x, status: 'applied' } : x)),
    };
}
/** Words per second the dialogue may need at most (the Fat Cutter ad fitted ~3.3). */
export const MAX_WORDS_PER_SECOND = 3.6;
/** Prompts are kept around this length: FreeVideo takes more, but focused prompts follow better. */
export const PROMPT_TARGET_CHARS = 2000; // 1800 + room for the "who speaks / who stays silent" sentence
const sentence = (t) => {
    const x = t.trim().replace(/\s+/g, ' ');
    if (!x)
        return '';
    return /[.!?…]$/.test(x) ? x : `${x}.`;
};
const lower = (t) => (t ? t[0].toLowerCase() + t.slice(1) : t);
const capitalise = (t) => (t ? t[0].toUpperCase() + t.slice(1) : t);
/** "A medium close-up at eye level, 50 mm lens, shallow depth of field." */
export function framingProse(shot) {
    const parts = [`${SHOT_SIZE_WORDS[shot.shotSize]} ${ANGLE_WORDS[shot.angle]}`, `${Math.round(shot.lensMm)} mm lens`];
    if (shot.depthOfField.trim())
        parts.push(lower(shot.depthOfField.trim().replace(/\.$/, '')));
    return sentence(parts.join(', '));
}
/** "The camera pushes in slowly with small amplitude." / "Static shot." / "Handheld camera, …" */
export function movementProse(m) {
    const extra = m.description.trim() ? ` ${capitalise(sentence(m.description))}` : '';
    if (m.type === 'static')
        return `Static shot.${extra}`;
    if (m.type === 'handheld')
        return `Handheld camera moving ${SPEED_WORDS[m.speed]} with ${m.amplitude} amplitude.${extra}`;
    return `The camera ${MOVE_VERBS[m.type]} ${SPEED_WORDS[m.speed]} with ${m.amplitude} amplitude.${extra}`;
}
const norm = (t) => t.trim().toLowerCase();
/** Words spoken in a plan shot. */
export const spokenWords = (shot) => shot.lines.reduce((n, l) => n + l.text.trim().split(/\s+/).filter(Boolean).length, 0);
/**
 * Turns the plan into storyboard shots. Camera (size, angle, lens, movement as prose with speed and amplitude),
 * lighting, grade, set and blocking go into the [Shot 1] description; the character and location bibles go in
 * word for word (as each character's description in the shot, used at first mention / in subject_definitions);
 * sound → overall_soundscape, music → non_diegetic_music. Picture references to other shots ("shot:<n>") are
 * left for the caller to replace with shot ids. Prompts longer than PROMPT_TARGET_CHARS lose their least
 * important sentences first (set dressing, grade, performance, composition, lighting).
 */
export function planToItems(plan, world) {
    const bible = new Map(plan.characters.map((c) => [norm(c.name), c]));
    const locations = new Map(plan.locations.map((l) => [norm(l.name), l]));
    const items = [];
    plan.shots.forEach((shot, index) => {
        const number = index + 1;
        if (shot.kind === 'card') {
            items.push({
                number,
                kind: 'card',
                data: cardDataSchema.parse({
                    text: (shot.cardText || shot.title || ' ').slice(0, 300),
                    seconds: Math.min(10, Math.max(1, shot.seconds)),
                }),
            });
            return;
        }
        // Who is in the shot: listed characters, speakers, and characters named in the action.
        const names = [];
        const add = (n) => {
            const key = norm(n);
            if (key && bible.has(key) && !names.some((x) => norm(x) === key))
                names.push(bible.get(key).name);
        };
        shot.characters.forEach(add);
        shot.lines.forEach((l) => add(l.speaker));
        for (const m of `${shot.blocking} ${shot.composition}`.matchAll(/\[([^\][]{1,80})\]/g))
            add(m[1]);
        const known = names
            .map((n) => ({ name: n, id: world.characterId(n), bible: bible.get(norm(n)) }))
            .filter((c) => c.id);
        const startFrom = shot.startFromPrevious && items.some((i) => i.kind === 'shot') ? 'previous' : null;
        // Reference pictures (never together with a start frame).
        const pictures = [];
        const pictureOf = new Map();
        if (!startFrom) {
            for (const r of shot.referencePictures) {
                if (pictures.length >= 3)
                    break;
                if (r.type === 'product' && world.productId && world.productHasPicture) {
                    if (pictureOf.has('product'))
                        continue;
                    pictures.push({ type: 'product', productId: world.productId });
                    pictureOf.set('product', pictures.length);
                }
                else if (r.type === 'character') {
                    const c = known.find((k) => norm(k.name) === norm(r.name));
                    if (!c?.id || !world.characterHasPicture(c.id) || pictureOf.has(`c:${c.id}`))
                        continue;
                    pictures.push({ type: 'character', characterId: c.id, imageIndex: 0 });
                    pictureOf.set(`c:${c.id}`, pictures.length);
                }
                else if (r.type === 'shot' && r.shotNumber > 0 && r.shotNumber < number) {
                    pictures.push({ type: 'shotFrame', shotId: `shot:${r.shotNumber}`, at: 'last' });
                }
            }
        }
        const productIn = !!world.productId && (shot.productInShot || pictureOf.has('product') || /\[product\]/i.test(shot.blocking));
        const cast = [];
        if (productIn)
            cast.push({ type: 'product', id: world.productId, note: '', picture: pictureOf.get('product') ?? null });
        for (const c of known) {
            cast.push({
                type: 'character',
                id: c.id,
                note: c.bible.appearance.trim(),
                picture: pictureOf.get(`c:${c.id}`) ?? null,
            });
        }
        // The description, sentence by sentence; optional ones can be dropped to keep the prompt focused.
        const loc = locations.get(norm(shot.location));
        const where = loc?.description.trim() || shot.location.trim();
        const mentioned = (n) => new RegExp(`\\[${n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'i').test(`${shot.blocking} ${shot.composition}`);
        const unnamed = known.filter((c) => !mentioned(c.name)).map((c) => `[${c.name}]`);
        const parts = [
            { text: framingProse(shot), drop: 0 },
            {
                text: where
                    ? sentence(`The scene is set in ${lower(where.replace(/\.$/, ''))}${shot.timeOfDay.trim() ? `, ${lower(shot.timeOfDay.trim().replace(/\.$/, ''))}` : ''}`)
                    : '',
                drop: 0,
            },
            { text: unnamed.length ? sentence(`In the frame: ${unnamed.join(', ')}`) : '', drop: 0 },
            // drop: the higher, the sooner it goes when the prompt is too long.
            // The action first: a character's first mention (where its full bible description goes) is in it.
            { text: sentence(shot.blocking), drop: 0 },
            { text: sentence(shot.composition), drop: 2 },
            { text: sentence(shot.setDressing), drop: 5 },
            { text: sentence(shot.performance), drop: 3 },
            { text: sentence(shot.lighting), drop: 1 },
            {
                text: shot.colorGrade.trim() ? sentence(`Colour grade: ${shot.colorGrade.trim().replace(/\.$/, '')}`) : '',
                drop: 4,
            },
        ];
        const lines = shot.lines
            .filter((l) => l.text.trim())
            .map((l, i) => {
            const c = known.find((k) => norm(k.name) === norm(l.speaker));
            const voice = [c?.bible.voice.trim(), l.emotion.trim()].filter(Boolean).join(', ');
            return {
                id: `d${number}-${i + 1}`,
                characterId: c?.id ?? null,
                speakerName: c ? '' : l.speaker.trim() || 'A voice',
                delivery: l.verb.trim() || 'says',
                voice: c ? voice : l.emotion.trim(),
                language: '',
                text: l.text,
                cut: 0,
            };
        });
        // Enough time for the words.
        const words = spokenWords(shot);
        const seconds = Math.min(SHOT_MAX_SECONDS, Math.max(SHOT_MIN_SECONDS, shot.seconds, Math.ceil(words / MAX_WORDS_PER_SECOND)));
        const notes = [
            shot.transition && `Transition: ${shot.transition}`,
            shot.continuity && `Continuity: ${shot.continuity}`,
        ]
            .filter(Boolean)
            .join('\n');
        const action = shot.blocking.trim().split(/(?<=[.!?])\s+/)[0] ?? '';
        const summary = action ? `${world.ctx.style.trim().replace(/[.,]$/, '')}: ${sentence(action)}` : '';
        const make = (dropLevel) => shotDataSchema.parse({
            label: (shot.title || `Shot ${number}`).slice(0, 120),
            seconds,
            style: '',
            look: '',
            picture: parts
                .filter((p) => p.text && p.drop <= dropLevel)
                .map((p) => p.text)
                .join(' '),
            camera: movementProse(shot.movement),
            cast,
            pictures,
            startFrom,
            lines,
            sound: shot.sound,
            music: shot.music,
            noOnScreenText: shot.noOnScreenText,
            // Shots with pictures: "<style>: <what happens>" (the first sentence of the action), as in the original ad.
            summary: summary,
            notes,
        });
        // Drop the least important sentences until the prompt is focused enough (level 5 keeps everything).
        let data = make(5);
        for (let level = 4; level >= 0 && buildPrompt(data, world.ctx).length > PROMPT_TARGET_CHARS; level--)
            data = make(level);
        items.push({ number, kind: 'shot', data });
    });
    return items;
}
